import React from 'react';
import { cleanDartPath, pointsToSvgPath } from '../../../utils/segPath';
import { HoverSvg } from '../styled';
import { SegmentationLabel } from 'models/segmentation';

interface HoverLayerProps {
  allSegmentations: SegmentationLabel[];
  visibleSegmentations: Set<string>;
  invalidOverlayIds: Set<string>;
  frameDimensions: { width: number; height: number };
  wrapperRef: React.RefObject<HTMLDivElement | null>;
  setHoveredSeg: React.Dispatch<
    React.SetStateAction<{ id: string; x: number; y: number; label: string; color?: string } | null>
  >;
  stroke?: boolean;
  hasMaskIds?: Set<string>;
  highlightedSegId?: string;
  highlightedStrokeColor?: string;
  enableHighlightedContour?: boolean;
  highlightedSegmentationClass?: string;
}

// Detect whether path is in custom backend format or standard SVG format
function detectPathFormat(path: string): 'custom' | 'standard' {
  const qCommands = path.match(/Q[^QML]+/g);
  if (!qCommands || qCommands.length === 0) return 'standard';

  const firstQ = qCommands[0];
  const coords = firstQ.match(/[-0-9.]+/g);

  return coords && coords.length >= 4 ? 'standard' : 'custom';
}

// Parse custom backend format using shared helpers (cleans long jumps/tails)
function parseCustomPathFormat(pathString: string): string {
  // Increase maxJump threshold to better accommodate long continuous contours
  // that may contain intermediate coordinate jumps; this reduces accidental
  // splitting of a single contour into multiple groups which caused clipping.
  const pts = cleanDartPath(pathString, 100);
  if (!pts || pts.length === 0) return '';
  return pointsToSvgPath(pts);
}

// Parse standard SVG format - just normalize whitespace and commas
function parseStandardPathFormat(pathString: string): string {
  return pathString
    .replace(/([MLCQZHVSA])\s*,/gi, '$1 ')
    .replace(/,/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Smart path normalizer that detects format and handles both
const normalizeSegPath = (raw: string): string => {
  if (!raw || raw.trim() === '') return '';

  const format = detectPathFormat(raw);

  if (format === 'custom') {
    return parseCustomPathFormat(raw);
  } else {
    return parseStandardPathFormat(raw);
  }
};

const SegHoverLayer: React.FC<HoverLayerProps> = ({
  allSegmentations,
  visibleSegmentations,
  invalidOverlayIds,
  frameDimensions,
  wrapperRef,
  setHoveredSeg,
  stroke = false,
  hasMaskIds,
  highlightedSegId,
  highlightedStrokeColor,
  enableHighlightedContour = false,
  highlightedSegmentationClass,
}) => {
  // Require natural frame dimensions (image natural size) to correctly map path coordinates.
  // If frameDimensions are not set the image is not loaded — rendering paths would distort,
  // so skip rendering until we have real frame dimensions.
  if (frameDimensions.width === 0 || frameDimensions.height === 0) return null;

  return (
    <HoverSvg
      width="100%"
      height="100%"
      viewBox={`0 0 ${frameDimensions.width} ${frameDimensions.height}`}
      preserveAspectRatio="xMidYMid meet"
    >
      {allSegmentations.map(seg => {
        if (!visibleSegmentations.has(seg.icid)) return null;
        if (invalidOverlayIds.has(seg.icid)) return null;
        if (hasMaskIds && !hasMaskIds.has(seg.icid)) return null;
        const rawPaths = Array.isArray(seg.paths) ? seg.paths : [];
        const d = rawPaths
          .map(p => normalizeSegPath(String(p)))
          .join(' ')
          .trim();
        if (!d) return null;
        const color = seg.segmentationColor || '#1890ff';
        const fillOpacity = 0.6; // slightly transparent fills
        const strokeWidth = stroke ? 3.0 : 1.5;
        // Highlight if either the highlightedSegId matches the segmentation icid
        // or if a highlightedSegmentationClass was provided and matches the segmentation's class value.
        // Normalize both class values (trim + lowercase) to allow case/whitespace-insensitive matching.
        const segClassRaw =
          (seg as any).segmentationClassValue ??
          (seg as any).segmentationClass ??
          (seg as any).segmentation_class ??
          null;
        const normalizedSegClass =
          typeof segClassRaw === 'string' ? segClassRaw.trim().toLowerCase() : null;
        const normalizedHighlightedClass =
          typeof highlightedSegmentationClass === 'string'
            ? highlightedSegmentationClass.trim().toLowerCase()
            : null;

        const isHighlighted =
          highlightedSegId === seg.icid ||
          (normalizedSegClass !== null &&
            normalizedHighlightedClass !== null &&
            normalizedSegClass === normalizedHighlightedClass);
        const highlightColor = highlightedStrokeColor || '#ffd700';
        return (
          <g key={seg.icid + '-svg'} style={{ pointerEvents: 'all' }}>
            <path
              d={d}
              fill={color}
              fillOpacity={fillOpacity}
              stroke={isHighlighted ? highlightColor : stroke ? color : 'none'}
              strokeWidth={isHighlighted ? Math.max(2, strokeWidth + 1) : stroke ? strokeWidth : 0}
              strokeOpacity={isHighlighted ? 1 : stroke ? 0.9 : 0}
              onMouseEnter={e =>
                setHoveredSeg({
                  id: seg.icid,
                  x:
                    (e.nativeEvent as MouseEvent).clientX -
                    (wrapperRef.current?.getBoundingClientRect().left || 0),
                  y:
                    (e.nativeEvent as MouseEvent).clientY -
                    (wrapperRef.current?.getBoundingClientRect().top || 0),
                  label: seg.segmentationClassValue,
                  color,
                })
              }
              onMouseMove={e =>
                setHoveredSeg(prev =>
                  prev && prev.id === seg.icid
                    ? {
                        ...prev,
                        x:
                          (e.nativeEvent as MouseEvent).clientX -
                          (wrapperRef.current?.getBoundingClientRect().left || 0),
                        y:
                          (e.nativeEvent as MouseEvent).clientY -
                          (wrapperRef.current?.getBoundingClientRect().top || 0),
                      }
                    : prev
                )
              }
              onMouseLeave={() => setHoveredSeg(null)}
            />
            {/* Extra bold contour for highlighted segmentation to ensure visible yellow outline
                even when fills are present. Render after the filled path and make it pointer-events none
                so it doesn't interfere with hover handling on the filled path. */}
            {isHighlighted && enableHighlightedContour && (
              <path
                d={d}
                fill="none"
                stroke={highlightColor}
                strokeWidth={Math.max(3, (stroke ? 3.0 : 1.5) + 2)}
                strokeOpacity={1}
                strokeLinejoin="round"
                strokeLinecap="round"
                className="highlighted-contour"
                style={{ pointerEvents: 'none' }}
              />
            )}
            {/* no debug marker */}
          </g>
        );
      })}
    </HoverSvg>
  );
};

export default SegHoverLayer;
